import type { ProviderCatalogPricing } from '../services/providerService';

/**
 * Renders a model's price for display next to its name.
 *
 * One formatter for every surface that lists models, so the image picker, the
 * agent bar and anything added later all read the same. Returns `undefined`
 * when there's no price to show — callers render nothing rather than "free" or
 * "$0.00", because an absent price means *unpriced* (a locally-hosted Ollama
 * model), not "costs nothing to run".
 *
 * Image models bill per image and land between $0.002 and $0.15, so sub-cent
 * prices need more than 2 decimals to avoid collapsing to "$0.00". LLM prices
 * are quoted per 1M tokens and are shown input-first, matching how providers
 * publish them.
 */
export function formatModelPrice(
  pricing: ProviderCatalogPricing | null | undefined,
): string | undefined {
  if (!pricing) return undefined;

  const symbol = currencySymbol(pricing.currency);

  if (pricing.pricePerImage != null && pricing.pricePerImage > 0) {
    return `${symbol}${trimZeros(pricing.pricePerImage)}/image`;
  }

  const input = pricing.inputTokensPer1M;
  const output = pricing.outputTokensPer1M;
  if ((input != null && input > 0) || (output != null && output > 0)) {
    return `${symbol}${trimZeros(input ?? 0)} in · ${symbol}${trimZeros(output ?? 0)} out /1M`;
  }

  return undefined;
}

/**
 * A longer form for tooltips, where there's room to name the unit outright.
 */
export function describeModelPrice(
  pricing: ProviderCatalogPricing | null | undefined,
): string | undefined {
  if (!pricing) return undefined;
  const symbol = currencySymbol(pricing.currency);

  if (pricing.pricePerImage != null && pricing.pricePerImage > 0) {
    return `Approximately ${symbol}${trimZeros(pricing.pricePerImage)} per generated image`;
  }
  const input = pricing.inputTokensPer1M;
  const output = pricing.outputTokensPer1M;
  if ((input != null && input > 0) || (output != null && output > 0)) {
    return `Approximately ${symbol}${trimZeros(input ?? 0)} per 1M input tokens and ${symbol}${trimZeros(output ?? 0)} per 1M output tokens`;
  }
  return undefined;
}

/**
 * Up to 3 decimals (so $0.0025 doesn't render as "$0.00"), with trailing zeros
 * dropped so $0.080 reads as "$0.08" and $7.00 as "$7".
 */
function trimZeros(value: number): string {
  return String(Number(value.toFixed(3)));
}

/** Symbol for the currencies the catalog actually emits; falls back to the code. */
function currencySymbol(currency: string | undefined): string {
  switch ((currency ?? 'USD').toUpperCase()) {
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return `${currency} `;
  }
}
